import { TextareaHTMLAttributes, forwardRef, useState, useEffect, useCallback, useRef } from 'react';
import { cn } from '@lib/utils/cn';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, helperText, className, ...props }, ref) => {
    const internalRef = useRef<HTMLTextAreaElement | null>(null);
    const [charCount, setCharCount] = useState(0);

    const maxLen = props.maxLength;
    const showCounter = maxLen != null && maxLen > 0;

    const mergedRef = useCallback((node: HTMLTextAreaElement | null) => {
      internalRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
    }, [ref]);

    // Sync character count on mount and when value prop changes
    useEffect(() => {
      if (showCounter && internalRef.current) {
        setCharCount(internalRef.current.value.length);
      }
    }, [showCounter, props.value]);

    // Listen for input events to update counter in real-time (works with uncontrolled inputs)
    useEffect(() => {
      const el = internalRef.current;
      if (!showCounter || !el) return;
      const handler = () => setCharCount(el.value.length);
      el.addEventListener('input', handler);
      return () => el.removeEventListener('input', handler);
    }, [showCounter]);

    const nearLimit = showCounter && maxLen! - charCount < maxLen! * 0.1;

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
            {label}
            {props.required && <span className="text-danger-500 ml-1">*</span>}
          </label>
        )}
        <textarea
          ref={mergedRef}
          className={cn(
            'block w-full rounded-lg border-secondary-300 dark:border-secondary-600',
            'bg-white dark:bg-secondary-800',
            'text-secondary-900 dark:text-secondary-100',
            'placeholder-secondary-400 dark:placeholder-secondary-500',
            'focus:border-primary-500 focus:ring-primary-500',
            'disabled:bg-secondary-100 dark:disabled:bg-secondary-900 disabled:cursor-not-allowed',
            error && 'border-danger-500 focus:border-danger-500 focus:ring-danger-500',
            className
          )}
          {...props}
        />
        <div className="flex justify-between mt-1">
          <div className="flex-1">
            {error && (
              <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p>
            )}
            {helperText && !error && (
              <p className="text-sm text-secondary-500 dark:text-secondary-400">{helperText}</p>
            )}
          </div>
          {showCounter && (
            <span className={cn(
              'text-xs ml-2 flex-shrink-0',
              nearLimit
                ? 'text-warning-600 dark:text-warning-400'
                : 'text-secondary-400 dark:text-secondary-500'
            )}>
              {charCount}/{maxLen}
            </span>
          )}
        </div>
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
